import adapters from './adapters';
import ConnectionMonitor from './connectionMonitor';
import INTERNAL from './internal';
import logger from './logger';
import Consumer from './consumer';

// Encapsulate the cable connection held by the consumer. This is an internal class not intended for direct user manipulation.

const { message_types, protocols } = INTERNAL;
const supportedProtocols           = protocols.slice(0, protocols.length - 1);

const indexOf = [].indexOf;

export default class Connection
{
    private consumer: Consumer;
    private subscriptions: any;
    private disconnected: boolean;
    private monitor: ConnectionMonitor;
    private webSocket: any;
    private events!: {[event: string]: (event: any) => void};
    private readonly reopenDelay: number;

    /**
     * Constructor
     */
    constructor(consumer: Consumer)
    {
        this.open          = this.open.bind(this);
        this.consumer      = consumer;
        this.subscriptions = this.consumer.subscriptions;
        this.monitor       = new ConnectionMonitor(this);
        this.disconnected  = true;
        this.reopenDelay   = 500;
        this.setEvents();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    send(data: unknown): boolean
    {
        if (this.isOpen())
        {
            this.webSocket.send(JSON.stringify(data));
            return true;
        }
        else
        {
            return false;
        }
    }

    open(): boolean
    {
        if (this.isActive())
        {
            logger.log(`Attempted to open WebSocket, but existing socket is ${this.getState()}`);
            return false;
        }
        else
        {
            logger.log(`Opening WebSocket, current state is ${this.getState()}, subprotocols: ${protocols}`);
            if (this.webSocket)
            {
                this.uninstallEventHandlers();
            }
            this.webSocket = new adapters.WebSocket(this.consumer.url, protocols);
            this.installEventHandlers();
            this.monitor.start();
            return true;
        }
    }

    close({ allowReconnect } = { allowReconnect: true }): boolean|void
    {
        if (!allowReconnect)
        {
            this.monitor.stop();
        }
        if (this.isActive())
        {
            return this.webSocket.close();
        }
    }

    reopen(): boolean|void
    {
        logger.log(`Reopening WebSocket, current state is ${this.getState()}`);
        if (this.isActive())
        {
            try
            {
                return this.close();
            } catch (error)
            {
                logger.log('Failed to reopen WebSocket', error);
            }
            finally
            {
                logger.log(`Reopening WebSocket in ${this.reopenDelay}ms`);
                setTimeout(this.open, this.reopenDelay);
            }
        }
        else
        {
            return this.open();
        }
    }

    getProtocol(): string|void
    {
        if (this.webSocket)
        {
            return this.webSocket.protocol;
        }
    }

    isOpen(): boolean
    {
        return this.isState('open');
    }

    isActive(): boolean
    {
        return this.isState('open', 'connecting');
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    private isProtocolSupported(): boolean
    {
        // @ts-ignore
        return indexOf.call(supportedProtocols, this.getProtocol()) >= 0;
    }

    private isState(...states: string[]): boolean
    {
        // @ts-ignore
        return indexOf.call(states, this.getState()) >= 0;
    }

    private getState(): string | null
    {
        if (this.webSocket)
        {
            for (const state in adapters.WebSocket)
            {
                if (adapters.WebSocket.hasOwnProperty(state) && (adapters.WebSocket as unknown as Record<string, number>)[state] === this.webSocket.readyState)
                {
                    return state.toLowerCase();
                }
            }
        }
        return null;
    }

    private installEventHandlers(): void
    {
        for (const eventName in this.events)
        {
            const handler                    = this.events[eventName].bind(this);
            this.webSocket[`on${eventName}`] = handler;
        }
    }

    private uninstallEventHandlers(): void
    {
        for (const eventName in this.events)
        {
            this.webSocket[`on${eventName}`] = function ()
            {
            };
        }
    }

    private setEvents(): void
    {
        const _this = this;

        this.events = {
            message(event): void
            {
                // @ts-ignore
                if (!this.isProtocolSupported())
                {
                    return;
                }
                const { identifier, message, reason, reconnect, type } = JSON.parse(event.data);
                switch (type)
                {
                    case message_types.welcome:
                        _this.monitor.recordConnect();
                        return _this.subscriptions.reload();
                    case message_types.disconnect:
                        logger.log(`Disconnecting. Reason: ${reason}`);
                        return _this.close({ allowReconnect: reconnect }) as void;
                    case message_types.ping:
                        return _this.monitor.recordPing();
                    case message_types.confirmation:
                        _this.subscriptions.confirmSubscription(identifier);
                        return _this.subscriptions.notify(identifier, 'connected');
                    case message_types.rejection:
                        return _this.subscriptions.reject(identifier);
                    default:
                        return _this.subscriptions.notify(identifier, 'received', message);
                }
            },

            open(): void
            {
                logger.log(`WebSocket onopen event, using '${_this.getProtocol()}' subprotocol`);
                _this.disconnected = false;
                if (!_this.isProtocolSupported())
                {
                    logger.log('Protocol is unsupported. Stopping monitor and disconnecting.');
                    return _this.close({ allowReconnect: false }) as void;
                }
            },

            close(_event): void
            {
                logger.log('WebSocket onclose event');
                if (_this.disconnected)
                {
                    return;
                }
                _this.disconnected = true;
                _this.monitor.recordDisconnect();
                return _this.subscriptions.notifyAll('disconnected', {
                    willAttemptReconnect: _this.monitor.isRunning()
                });
            },

            error(): void
            {
                logger.log('WebSocket onerror event');
            }
        };
    }
}

